/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  message,
  Table,
  Select,
  Button,
  Card,
  Row,
  Col,
  Progress,
  Tabs,
  Typography,
  Tag,
  Alert,
  Space,
  Avatar,
  Divider,
  List,
  Badge,
  Empty,
} from "antd";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from "recharts";
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  DollarOutlined,
  CalendarOutlined,
  ShoppingOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { reportsApi } from "../../apis";
import { hasPermission } from "../../utils/permissions";
import ResponsiveTable from "../../_components/ResponsiveTable";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const COLORS = {
  primary: "#7B4D12",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
};

const ReportsPage = () => {
  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [revenuePerTrip, setRevenuePerTrip] = useState([]);
  const [reservationsPerDate, setReservationsPerDate] = useState([]);
  const [utilization, setUtilization] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerTotal, setCustomerTotal] = useState(0);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [customerPage, setCustomerPage] = useState(1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const currentUser = JSON.parse(localStorage.getItem("user") || "null");
  const userPermissions = currentUser?.permissions || [];

  // Check permissions
  const canViewReports = hasPermission(userPermissions, "reports.view");
  const canViewRevenue = canViewReports;
  const canViewUtilization = canViewReports;
  const canViewCustomers = canViewReports;

  const MONTH_NAMES = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("en-GB", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    if (canViewReports) {
      fetchAll();
    }
  }, [year, canViewReports]);

  useEffect(() => {
    if (canViewReports && canViewCustomers) {
      fetchCustomers();
    }
  }, [customerPage, canViewReports, canViewCustomers]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const promises = [];

      if (canViewRevenue) {
        promises.push(
          reportsApi.getRevenuePerTrip(),
          reportsApi.getMonthlyRevenue(year),
        );
      }

      if (canViewUtilization) {
        promises.push(reportsApi.getSeatUtilization());
      }

      const results = await Promise.all(promises);
      let resultIndex = 0;

      if (canViewRevenue) {
        const [rev, monthly] = [results[resultIndex++], results[resultIndex++]];
        setRevenuePerTrip(Array.isArray(rev) ? rev : []);
        const monthlyArr = Array.isArray(monthly) ? monthly : [];
        setMonthlyRevenue(
          MONTH_NAMES.map((name, i) => {
            const f = monthlyArr.find((r) => Number(r.month) === i + 1);
            return { month: name, revenue: f ? Number(f.revenue) : 0 };
          }),
        );
      }

      if (canViewUtilization) {
        const util = results[resultIndex++];
        setUtilization(Array.isArray(util) ? util : []);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        message.error("You don't have permission to view reports");
      } else {
        message.error("Failed to load reports");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    if (!canViewCustomers) return;
    try {
      const r = await reportsApi.getCustomers({
        page: customerPage,
        limit: isMobile ? 10 : 20,
      });
      setCustomers(r.data || []);
      setCustomerTotal(r.total || 0);
    } catch (error) {
      if (error.response?.status === 403) {
        message.error("You don't have permission to view customer data");
      } else {
        message.error("Failed to load customers");
      }
    }
  };

  const revColumns = [
    {
      title: "Trip",
      dataIndex: "tripName",
      key: "trip",
      render: (n) => <strong>{n}</strong>,
      width: isMobile ? 150 : undefined,
    },
    {
      title: "Revenue",
      dataIndex: "totalRevenue",
      key: "rev",
      render: (v) => `EGP ${Number(v).toLocaleString()}`,
      width: isMobile ? 120 : undefined,
    },
    { 
      title: "Seats Sold", 
      dataIndex: "totalSeats", 
      key: "seats",
      width: isMobile ? 100 : undefined,
    },
    { 
      title: "Reservations", 
      dataIndex: "totalReservations", 
      key: "res",
      width: isMobile ? 100 : undefined,
    },
  ];

  const utilColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (d) => isMobile ? formatDateTime(d).split(',')[0] : formatDateTime(d),
      width: isMobile ? 100 : undefined,
    },
    { 
      title: "Trip", 
      dataIndex: "tripName", 
      key: "trip",
      width: isMobile ? 120 : undefined,
    },
    { 
      title: "Deck", 
      dataIndex: "deckName", 
      key: "deck",
      width: isMobile ? 100 : undefined,
    },
    {
      title: "Utilization",
      key: "util",
      render: (_, r) => (
        <div style={{ minWidth: isMobile ? 100 : 120 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 4,
            }}
          >
            <Text style={{ fontSize: 12 }}>
              {r.reservedSeats}/{r.capacity}
            </Text>
            <Text
              strong
              style={{
                fontSize: 12,
                color:
                  r.utilizationPct >= 80
                    ? COLORS.success
                    : r.utilizationPct >= 50
                    ? COLORS.warning
                    : COLORS.danger,
              }}
            >
              {r.utilizationPct}%
            </Text>
          </div>
          <Progress
            percent={r.utilizationPct}
            size="small"
            showInfo={false}
            strokeColor={
              r.utilizationPct >= 80
                ? COLORS.success
                : r.utilizationPct >= 50
                ? COLORS.warning
                : COLORS.danger
            }
          />
        </div>
      ),
      width: isMobile ? 120 : undefined,
    },
  ];

  // Mobile-friendly customer card view
  const CustomerCard = ({ customer }) => {
    const fullName = `${customer.firstName || ""} ${customer.lastName || ""}`.trim() || "Anonymous";
    const initials = fullName
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
    
    return (
      <Card 
        style={{ 
          marginBottom: 12, 
          borderRadius: 12,
          cursor: "pointer",
          transition: "all 0.3s ease",
        }}
        hoverable
        bodyStyle={{ padding: "16px" }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <Avatar 
            size={48} 
            style={{ 
              backgroundColor: COLORS.primary,
              flexShrink: 0,
            }}
          >
            {initials || <UserOutlined />}
          </Avatar>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text strong style={{ fontSize: 16, display: "block", marginBottom: 4 }}>
              {fullName}
            </Text>
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              {customer.email && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <MailOutlined style={{ fontSize: 12, color: "#8c8c8c" }} />
                  <Text style={{ fontSize: 12 }} ellipsis>
                    {customer.email}
                  </Text>
                </div>
              )}
              {customer.phone && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <PhoneOutlined style={{ fontSize: 12, color: "#8c8c8c" }} />
                  <Text style={{ fontSize: 12 }}>{customer.phone}</Text>
                </div>
              )}
            </Space>
          </div>
        </div>
        
        <Divider style={{ margin: "12px 0" }} />
        
        <Row gutter={[12, 12]}>
          <Col span={12}>
            <div style={{ textAlign: "center" }}>
              <ShoppingOutlined style={{ fontSize: 20, color: COLORS.primary, marginBottom: 4 }} />
              <div>
                <Text strong style={{ fontSize: 18 }}>
                  {customer.totalReservations || 0}
                </Text>
                <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                  Reservations
                </Text>
              </div>
            </div>
          </Col>
          <Col span={12}>
            <div style={{ textAlign: "center" }}>
              <DollarOutlined style={{ fontSize: 20, color: COLORS.primary, marginBottom: 4 }} />
              <div>
                <Text strong style={{ fontSize: 18 }}>
                  EGP {Number(customer.totalSpend || 0).toLocaleString()}
                </Text>
                <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                  Total Spend
                </Text>
              </div>
            </div>
          </Col>
        </Row>
        
        {customer.joinedAt && (
          <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8 }}>
            <CalendarOutlined style={{ fontSize: 12, color: "#8c8c8c" }} />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Joined {new Date(customer.joinedAt).toLocaleDateString()}
            </Text>
          </div>
        )}
      </Card>
    );
  };

  const customerColumns = [
    {
      title: "Name",
      key: "name",
      render: (_, r) =>
        `${r.firstName || ""} ${r.lastName || ""}`.trim() || "-",
      width: isMobile ? 150 : undefined,
    },
    { 
      title: "Email", 
      dataIndex: "email", 
      key: "email",
      width: isMobile ? 180 : undefined,
      render: (v) => isMobile && v?.length > 20 ? `${v.substring(0, 20)}...` : v,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (v) => v || "-",
      width: isMobile ? 120 : undefined,
    },
    { 
      title: "Reservations", 
      dataIndex: "totalReservations", 
      key: "res",
      width: isMobile ? 100 : undefined,
    },
    {
      title: "Total Spend",
      dataIndex: "totalSpend",
      key: "spend",
      render: (v) => `EGP ${Number(v || 0).toLocaleString()}`,
      width: isMobile ? 120 : undefined,
    },
    {
      title: "Joined",
      dataIndex: "joinedAt",
      key: "joined",
      render: (d) => new Date(d).toLocaleDateString(),
      width: isMobile ? 100 : undefined,
    },
  ];

  const currentYear = new Date().getFullYear();

  // Permission check for viewing the page
  if (!canViewReports) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <Alert
              message="Access Denied"
              description="You don't have permission to view reports. Please contact your administrator if you believe this is an error."
              type="error"
              showIcon
            />
          </div>
        </div>
      </div>
    );
  }

  // Get available tabs based on permissions
  const getAvailableTabs = () => {
    const tabs = [];

    if (canViewRevenue) {
      tabs.push({
        key: "revenue",
        label: "Revenue",
        content: (
          <>
            <Card
              title={
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 600 }}>Monthly Revenue</span>
                  <Select
                    value={year}
                    onChange={(v) => setYear(v)}
                    size="small"
                    style={{ width: 90 }}
                  >
                    {[currentYear - 2, currentYear - 1, currentYear].map(
                      (y) => (
                        <Option key={y} value={y}>
                          {y}
                        </Option>
                      ),
                    )}
                  </Select>
                </div>
              }
              style={{ borderRadius: 12, marginBottom: 16 }}
            >
              <ResponsiveContainer width="100%" height={isMobile ? 250 : 280}>
                <BarChart
                  data={monthlyRevenue}
                  margin={{ top: 5, right: 10, left: isMobile ? -20 : 0, bottom: 5 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f0f0f5"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    axisLine={false}
                    tickLine={false}
                    interval={isMobile ? 1 : 0}
                  />
                  <YAxis
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => isMobile ? `${(v / 1000).toFixed(0)}k` : `${(v / 1000).toFixed(0)}k`}
                  />
                  <RTooltip
                    formatter={(v) => [
                      `EGP ${Number(v).toLocaleString()}`,
                      "Revenue",
                    ]}
                    contentStyle={{ borderRadius: 8 }}
                  />
                  <Bar
                    dataKey="revenue"
                    fill={COLORS.primary}
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card
              title={<span style={{ fontWeight: 600 }}>Revenue Per Trip</span>}
              style={{ borderRadius: 12 }}
            >
              <ResponsiveTable
                columns={revColumns}
                dataSource={revenuePerTrip}
                rowKey="tripId"
                pagination={false}
                scroll={{ x: isMobile ? 500 : true }}
                loading={loading}
              />
            </Card>
          </>
        ),
      });
    }

    if (canViewUtilization) {
      tabs.push({
        key: "utilization",
        label: "Seat Utilization",
        content: (
          <Card style={{ borderRadius: 12 }}>
            <ResponsiveTable
              columns={utilColumns}
              dataSource={utilization}
              rowKey={(_, i) => i}
              scroll={{ x: isMobile ? 550 : true }}
              loading={loading}
              pagination={{ 
                pageSize: isMobile ? 10 : 20, 
                showSizeChanger: !isMobile,
                size: isMobile ? "small" : "default",
              }}
            />
          </Card>
        ),
      });
    }

    if (canViewCustomers) {
      tabs.push({
        key: "customers",
        label: "Customers",
        content: (
          <Card style={{ borderRadius: 12 }}>
            {isMobile ? (
              // Mobile view: Card-based layout
              <>
                {customers.length === 0 ? (
                  <Empty description="No customers found" />
                ) : (
                  <>
                    <List
                      dataSource={customers}
                      renderItem={(customer) => (
                        <CustomerCard customer={customer} />
                      )}
                      loading={loading}
                    />
                    <div style={{ marginTop: 16, textAlign: "center" }}>
                      <Space>
                        <Button
                          size="small"
                          disabled={customerPage === 1}
                          onClick={() => setCustomerPage(customerPage - 1)}
                        >
                          Previous
                        </Button>
                        <Text type="secondary">
                          Page {customerPage} of {Math.ceil(customerTotal / (isMobile ? 10 : 20))}
                        </Text>
                        <Button
                          size="small"
                          disabled={customerPage * (isMobile ? 10 : 20) >= customerTotal}
                          onClick={() => setCustomerPage(customerPage + 1)}
                        >
                          Next
                        </Button>
                      </Space>
                    </div>
                  </>
                )}
              </>
            ) : (
              // Desktop view: Table layout
              <Table
                columns={customerColumns}
                dataSource={customers}
                rowKey="userId"
                scroll={{ x: 800 }}
                pagination={{
                  current: customerPage,
                  pageSize: 20,
                  total: customerTotal,
                  onChange: setCustomerPage,
                  showTotal: (t, r) => `${r[0]}-${r[1]} of ${t}`,
                  size: "default",
                }}
                loading={loading}
              />
            )}
          </Card>
        ),
      });
    }

    return tabs;
  };

  const availableTabs = getAvailableTabs();

  // If no tabs are available
  if (availableTabs.length === 0) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <Alert
              message="No Reports Available"
              description="You don't have permission to view any reports. Please contact your administrator."
              type="warning"
              showIcon
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header">
            <div className="content-page-header" style={{ flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "center", gap: isMobile ? 12 : 0 }}>
              <h5 style={{ marginBottom: isMobile ? 8 : 0 }}>Reports & Analytics</h5>
              <Tag color="blue" style={{ marginLeft: isMobile ? 0 : 12 }}>
                {availableTabs.length} Report Type
                {availableTabs.length !== 1 ? "s" : ""} Available
              </Tag>
            </div>
          </div>

          {!isMobile && (
            <Alert
              message="Report Access"
              description={`You have access to ${
                availableTabs.length
              } report section${availableTabs.length !== 1 ? "s" : ""}. ${
                availableTabs.length < 3
                  ? "Contact an administrator if you need additional report access."
                  : "You have full report access."
              }`}
              type="info"
              showIcon
              className="mb-4"
            />
          )}

          <Tabs 
            defaultActiveKey={availableTabs[0].key} 
            size={isMobile ? "small" : "large"}
            tabBarStyle={{ marginBottom: 16 }}
          >
            {availableTabs.map((tab) => (
              <TabPane tab={tab.label} key={tab.key}>
                {tab.content}
              </TabPane>
            ))}
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;